import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrderService } from '../../../api';
import { useCalendar } from '../../../calendar/CalendarContext/CalendarContext';
import { SectionType } from '../sectionMeta';
import { UnifiedRow } from './UnifiedRow';

const ORDER_LIKE = new Set<SectionType>([
  SectionType.EVENTS,
  SectionType.PRIVATE_SESSIONS,
  SectionType.COURSES,
  SectionType.SAVED_CARD_CHARGES,
  SectionType.POS_CHARGES,
]);

const PASS_LIKE = new Set<SectionType>([
  SectionType.PASSES,
  SectionType.RECURRING_PASSES,
  SectionType.RECURRING_PASS_CHARGES,
]);

export const useUnifiedRowClickHandler = () => {
  const navigate = useNavigate();
  const { openEditOrderDrawer } = useCalendar();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const openOrderById = useCallback(
    async (orderId: string): Promise<boolean> => {
      setLoadingId(orderId);
      try {
        const order = await OrderService.getOrder(orderId);
        openEditOrderDrawer({ order, orderPageId: order.order_page_id, parentId: order.parent_id });
        return true;
      } catch {
        return false;
      } finally {
        setLoadingId(null);
      }
    },
    [openEditOrderDrawer],
  );

  const onRowClick = useCallback(
    async (row: UnifiedRow) => {
      if (ORDER_LIKE.has(row.sectionType)) {
        await openOrderById(row.entityId);
        return;
      }
      if (row.sectionType === SectionType.PAYMENT_LINKS) {
        const ok = await openOrderById(row.entityId);
        if (!ok) navigate('/dashboard/payment-links');
        return;
      }
      if (PASS_LIKE.has(row.sectionType)) {
        navigate(`/dashboard/passes?entityid=${encodeURIComponent(row.entityId)}`);
        return;
      }
      if (row.sectionType === SectionType.VOUCHERS) {
        navigate('/dashboard/vouchers');
      }
    },
    [openOrderById, navigate],
  );

  return { onRowClick, loadingId };
};
